import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Search,
  Delete,
  Email,
  CalendarToday,
  ShoppingCart,
  AttachMoney,
  ExpandMore,
  Person,
} from '@mui/icons-material';
import customerService from '../services/customerService';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (customers && Array.isArray(customers)) {
      const filtered = customers.filter((customer) =>
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers([]);
    }
  }, [searchTerm, customers]);

  const fetchCustomers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await customerService.getCustomers();
      if (response.success) {
        setCustomers(response.customers || []);
      } else {
        setError(response.message || 'Failed to fetch customers');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch customers');
      console.error('Error fetching customers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = async (customer) => {
    try {
      const response = await customerService.getCustomer(customer._id);
      if (response.success) {
        setSelectedCustomer(response.customer);
        setOpenDialog(true);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to fetch customer details',
        severity: 'error'
      });
    }
  };

  const handleUnsubscribe = async (id) => {
    if (window.confirm('Are you sure you want to unsubscribe this customer?')) {
      try {
        const response = await customerService.unsubscribeCustomer(id);
        if (response.success) {
          setSnackbar({
            open: true,
            message: 'Customer unsubscribed successfully',
            severity: 'success'
          });
          fetchCustomers();
          if (openDialog) {
            setOpenDialog(false);
          }
        }
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.response?.data?.message || 'Failed to unsubscribe customer',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCustomer(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `PKR ${amount?.toLocaleString() || '0.00'}`;
  };

  const stats = {
    total: customers?.length || 0,
    withOrders: customers?.filter(c => c.orderCount > 0).length || 0,
    totalRevenue: customers?.reduce((sum, c) => sum + (c.totalSpent || 0), 0) || 0,
    newThisMonth: customers?.filter(c => {
      if (!c.subscribedAt) return false;
      const subscribed = new Date(c.subscribedAt);
      const now = new Date();
      return subscribed.getMonth() === now.getMonth() && subscribed.getFullYear() === now.getFullYear();
    }).length || 0,
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          color: '#000000',
          fontWeight: 700,
          mb: 3,
          pb: 2,
          borderBottom: '2px solid #ff2852',
        }}
      >
        Customers
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: '#666666', fontSize: '0.875rem', mb: 1 }}>
                    Total Subscribers
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                    {stats.total}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#ff2852',
                    borderRadius: '50%',
                    width: 60,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#000000',
                  }}
                >
                  <Person />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: '#666666', fontSize: '0.875rem', mb: 1 }}>
                    With Orders
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700 }}>
                    {stats.withOrders}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#ff2852',
                    borderRadius: '50%',
                    width: 60,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  <ShoppingCart />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: '#666666', fontSize: '0.875rem', mb: 1 }}>
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700, fontSize: '1.5rem' }}>
                    {formatCurrency(stats.totalRevenue)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#ff2852',
                    borderRadius: '50%',
                    width: 60,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  <AttachMoney />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: '#666666', fontSize: '0.875rem', mb: 1 }}>
                    New This Month
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#000000', fontWeight: 700 }}>
                    {stats.newThisMonth}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: '#ff2852',
                    borderRadius: '50%',
                    width: 60,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                  }}
                >
                  <CalendarToday />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
        <TextField
          fullWidth
          placeholder="Search customers by email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#e0e0e0',
              },
              '&:hover fieldset': {
                borderColor: '#ff2852',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#ff2852',
              },
            },
          }}
        />
      </Paper>

      {/* Customers Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Subscribed Date</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#000000' }} align="center">Orders</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#000000' }} align="right">Total Spent</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#000000' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchTerm ? 'No customers found matching your search' : 'No customers found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCustomers.map((customer) => (
                <TableRow
                  key={customer._id}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#fafafa',
                    },
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Email sx={{ color: '#666666', fontSize: 18 }} />
                      <Typography sx={{ fontWeight: 500 }}>{customer.email}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday sx={{ color: '#666666', fontSize: 18 }} />
                      <Typography>{formatDate(customer.subscribedAt)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={customer.orderCount || 0}
                      sx={{
                        backgroundColor: customer.orderCount > 0 ? '#4caf50' : '#e0e0e0',
                        color: customer.orderCount > 0 ? '#ffffff' : '#666666',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontWeight: 600, color: '#000000' }}>
                      {formatCurrency(customer.totalSpent || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1} justifyContent="center">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(customer)}
                        sx={{
                          color: '#ff2852',
                          '&:hover': {
                            backgroundColor: 'rgba(123, 44, 191, 0.1)',
                          },
                        }}
                      >
                        <Search />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleUnsubscribe(customer._id)}
                        sx={{
                          color: '#f44336',
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Customer Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '2px solid #ff2852', pb: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Email sx={{ color: '#ff2852', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000' }}>
                Customer Details
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666', mt: 0.5 }}>
                {selectedCustomer?.email}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedCustomer && (
            <Box>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#fafafa' }}>
                    <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                      Subscribed Date
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatDate(selectedCustomer.subscribedAt)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#fafafa' }}>
                    <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                      Total Orders
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedCustomer.orderCount || 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#fafafa' }}>
                    <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                      Total Spent
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#ff2852' }}>
                      {formatCurrency(selectedCustomer.totalSpent || 0)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#fafafa' }}>
                    <Typography variant="body2" sx={{ color: '#666666', mb: 1 }}>
                      Status
                    </Typography>
                    <Chip
                      label={selectedCustomer.isActive ? 'Active' : 'Inactive'}
                      sx={{
                        backgroundColor: selectedCustomer.isActive ? '#4caf50' : '#f44336',
                        color: '#ffffff',
                        fontWeight: 600,
                      }}
                    />
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#000000' }}>
                Order History
              </Typography>

              {selectedCustomer.orders && selectedCustomer.orders.length > 0 ? (
                selectedCustomer.orders.map((order, index) => (
                  <Accordion key={order._id || index} sx={{ mb: 1, border: '1px solid #e0e0e0' }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={2}>
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>
                            Order #{order.orderNumber}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666666', mt: 0.5 }}>
                            {formatDate(order.createdAt)}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={2} alignItems="center">
                          <Chip
                            label={order.status || order.orderStatus || 'pending'}
                            sx={{
                              backgroundColor:
                                order.status === 'delivered' || order.orderStatus === 'delivered'
                                  ? '#4caf50'
                                  : order.status === 'cancelled' || order.orderStatus === 'cancelled'
                                    ? '#f44336'
                                    : '#ff2852',
                              color: '#ffffff',
                              fontWeight: 600,
                            }}
                            size="small"
                          />
                          <Typography sx={{ fontWeight: 600, color: '#000000' }}>
                            {formatCurrency(order.totalAmount || order.total || 0)}
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Items:
                        </Typography>
                        {order.items && order.items.length > 0 ? (
                          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                            {order.items.map((item, idx) => (
                              <li key={idx}>
                                <Typography variant="body2">
                                  {item.product?.name || 'Product'} - Qty: {item.quantity} - {formatCurrency(item.price)}
                                </Typography>
                              </li>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#666666', mb: 2 }}>
                            No items found
                          </Typography>
                        )}
                        {order.shippingAddress && (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                              Shipping Address:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666666' }}>
                              {order.shippingAddress.name}<br />
                              {order.shippingAddress.street}<br />
                              {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#fafafa' }}>
                  <Typography color="text.secondary">
                    No orders found for this customer
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: '#666666',
              textTransform: 'none',
            }}
          >
            Close
          </Button>
          {selectedCustomer && (
            <Button
              onClick={() => handleUnsubscribe(selectedCustomer._id)}
              variant="contained"
              sx={{
                backgroundColor: '#f44336',
                color: '#ffffff',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d32f2f',
                },
              }}
            >
              Unsubscribe
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 9999,
            minWidth: 300,
          }}
        >
          {snackbar.message}
        </Alert>
      )}
    </Box>
  );
};

export default Customers;







